import { useEffect, useState, useMemo, FC, useCallback } from "react"
import { Card, Container, Row, Col, Spinner, Table, Badge, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import API from "../common/API"
import { parsedUser } from "../common/GetCurrentUser"
import { FormatMoney } from "../common/helpers"
import { DispatchSellerSummary } from "../types/Indicators.types"
import moment from "moment"

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
)

interface SellerSalesData {
    vendedorId: number | null
    vendedorNombre: string
    year: number
    month: number
    amount: number
}

interface ProcessedData {
    labels: string[]
    datasets: {
        label: string
        data: number[]
        backgroundColor: string
        borderColor: string
        borderWidth: number
        borderRadius: number
    }[]
}

const SalesBySellerPage: FC = () => {
    const { t } = useTranslation("global")
    const user = parsedUser()
    const [loading, setLoading] = useState<boolean>(true)
    const [salesData, setSalesData] = useState<SellerSalesData[]>([])
    const [error, setError] = useState<string | null>(null)
    const [selectedSeller, setSelectedSeller] = useState<string | null>(null)
    const [dispatchSummary, setDispatchSummary] = useState<DispatchSellerSummary | null>(null)
    const [loadingDispatch, setLoadingDispatch] = useState(false)
    const [dispatchStartDate, setDispatchStartDate] = useState(moment().startOf("year").format("YYYY-MM-DD"))
    const [dispatchEndDate, setDispatchEndDate] = useState(moment().format("YYYY-MM-DD"))
    const [showDetailedData, setShowDetailedData] = useState(false)

    useEffect(() => {
        const fetchSalesData = async () => {
            if (!user?.OrganizationId) {
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                const response = await API.getAction(
                    `dashboard/SalesBySellerGeneral?OrganizationId=${user.OrganizationId}`
                )

                console.log("Seller Sales API Response:", response)

                if (response?.data?.response && Array.isArray(response.data.response)) {
                    console.log("Seller Sales Data:", response.data.response)
                    setSalesData(response.data.response)
                    // Set first seller as default
                    const sellers = Array.from(
                        new Set(response.data.response.map((d: SellerSalesData) => d.vendedorNombre))
                    )
                    if (sellers.length > 0) {
                        setSelectedSeller(sellers[0] as string)
                    }
                } else {
                    console.warn("No valid response data:", response?.data)
                    setSalesData([])
                }
                setError(null)
            } catch (err) {
                console.error("Error fetching seller sales data:", err)
                setError((err as Error)?.message || "Failed to load seller sales data")
                setSalesData([])
            } finally {
                setLoading(false)
            }
        }

        fetchSalesData()
    }, [user?.OrganizationId])

    const loadDispatchSummary = useCallback(async (sellerName: string, start: string, end: string) => {
        if (!user?.OrganizationId) return
        const sellerRow = salesData.find(d => d.vendedorNombre === sellerName && d.vendedorId != null)
        if (!sellerRow?.vendedorId) return
        try {
            setLoadingDispatch(true)
            const params = `organizationId=${user.OrganizationId}&externalSellerId=${sellerRow.vendedorId}&startDate=${start}&endDate=${end}`
            const res = await API.getAction("Indicators/DispatchSellerSummary", params)
            if (res.data.status === "ok") setDispatchSummary(res.data.response)
        } catch (ex) {
            console.error(ex)
        } finally {
            setLoadingDispatch(false)
        }
    }, [user?.OrganizationId, salesData])

    useEffect(() => {
        if (selectedSeller) loadDispatchSummary(selectedSeller, dispatchStartDate, dispatchEndDate)
        else setDispatchSummary(null)
    }, [selectedSeller])

    // Get unique sellers
    const sellers = useMemo(() => {
        return Array.from(
            new Set(
                salesData
                    .filter(
                        (d) =>
                            d.vendedorNombre != null &&
                            d.month >= 1 &&
                            d.month <= 12 &&
                            d.amount != null
                    )
                    .map((d) => d.vendedorNombre)
            )
        ).sort()
    }, [salesData])

    // Process data for selected seller
    const chartData: ProcessedData | null = useMemo(() => {
        if (!selectedSeller || !salesData || salesData.length === 0) return null

        // Filter data for selected seller
        const sellerData = salesData.filter(
            (d) =>
                d.vendedorNombre === selectedSeller &&
                d.year != null &&
                d.month != null &&
                d.month >= 1 &&
                d.month <= 12 &&
                d.amount != null
        )

        if (sellerData.length === 0) return null

        // Get unique years and sort them
        const years = Array.from(new Set(sellerData.map((d) => d.year))).sort()

        const monthNames = [
            t("Jan"), t("Feb"), t("Mar"), t("Apr"), t("May"), t("Jun"),
            t("Jul"), t("Aug"), t("Sep"), t("Oct"), t("Nov"), t("Dec")
        ]

        // Create a map for each year
        const yearDataMap = new Map<number, Map<number, number>>()

        years.forEach((year) => {
            yearDataMap.set(year, new Map())
        })

        // Fill the map with sales data
        sellerData.forEach((item) => {
            const yearMap = yearDataMap.get(item.year)
            if (yearMap) {
                yearMap.set(item.month, item.amount || 0)
            }
        })

        // Find all months that have data
        const allMonths = Array.from(
            new Set(sellerData.map((d) => d.month))
        ).sort((a, b) => a - b)

        // Colors for each year
        const colors = [
            { bg: "rgba(59, 130, 246, 0.7)", border: "rgba(59, 130, 246, 1)" },
            { bg: "rgba(16, 185, 129, 0.7)", border: "rgba(16, 185, 129, 1)" },
            { bg: "rgba(245, 158, 11, 0.7)", border: "rgba(245, 158, 11, 1)" },
            { bg: "rgba(239, 68, 68, 0.7)", border: "rgba(239, 68, 68, 1)" },
            { bg: "rgba(168, 85, 247, 0.7)", border: "rgba(168, 85, 247, 1)" },
        ]

        const datasets = years
            .map((year, index) => {
                const yearMap = yearDataMap.get(year)
                if (!yearMap || year == null) return null
                const color = colors[index % colors.length]

                return {
                    label: String(year),
                    data: allMonths.map((month) => yearMap.get(month) || 0),
                    backgroundColor: color.bg,
                    borderColor: color.border,
                    borderWidth: 2,
                    borderRadius: 6,
                }
            })
            .filter((dataset): dataset is NonNullable<typeof dataset> => dataset !== null)

        return {
            labels: allMonths.map((m) => (m >= 1 && m <= 12 ? monthNames[m - 1] : "")),
            datasets,
        }
    }, [salesData, selectedSeller])

    // Calculate totals for selected seller
    const sellerTotals = useMemo(() => {
        if (!selectedSeller || !salesData || salesData.length === 0) return {}

        const validData = salesData.filter(
            (d) =>
                d.vendedorNombre === selectedSeller &&
                d.year != null &&
                d.amount != null
        )

        const years = Array.from(new Set(validData.map((d) => d.year))).filter((y) => y != null)
        const totalsByYear: Record<number, number> = {}

        years.forEach((year) => {
            totalsByYear[year] = validData
                .filter((d) => d.year === year)
                .reduce((sum, d) => sum + (d.amount || 0), 0)
        })

        return totalsByYear
    }, [salesData, selectedSeller])

    const sellerGrandTotal = useMemo(() => {
        if (!selectedSeller || !salesData || salesData.length === 0) return 0

        return salesData
            .filter(
                (d) =>
                    d.vendedorNombre === selectedSeller &&
                    d.amount != null
            )
            .reduce((sum, d) => sum + (d.amount || 0), 0)
    }, [salesData, selectedSeller])

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top" as const,
                labels: {
                    font: {
                        size: 14,
                        weight: "bold" as const,
                    },
                    padding: 20,
                },
            },
            title: {
                display: true,
                text: `${t("Sales")} - ${selectedSeller} (${t("Year Comparison")})`,
                font: {
                    size: 20,
                    weight: "bold" as const,
                },
                padding: {
                    bottom: 30,
                },
            },
            tooltip: {
                callbacks: {
                    label: function (context: { dataset: { label?: string }, parsed: { y: number | null } }) {
                        let label = context.dataset.label || ""
                        if (label) {
                            label += ": "
                        }
                        if (context.parsed.y !== null) {
                            label += FormatMoney(context.parsed.y, 2)
                        }
                        return label
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 12,
                        weight: "bold" as const,
                    },
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: "rgba(0, 0, 0, 0.05)",
                },
                ticks: {
                    font: {
                        size: 12,
                    },
                    callback: function (value: string | number) {
                        return FormatMoney(Number(value), 0)
                    },
                },
            },
        },
    }

    if (!user?.OrganizationId) {
        return (
            <Container fluid>
                <Card className="my-4">
                    <Card.Body>{t("Organization not selected")}</Card.Body>
                </Card>
            </Container>
        )
    }

    return (
        <Container fluid className="px-4">
            <Row className="my-4">
                <Col>
                    <h2 className="mb-0">
                        {t("Sales By Seller")} - {t("Year Comparison")}
                    </h2>
                </Col>
            </Row>

            {/* Seller Selector */}
            {sellers.length > 0 && (
                <Row className="mb-4">
                    <Col md={6} lg={4}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <label className="form-label fw-bold">{t("Select Seller")}</label>
                                <select
                                    className="form-select"
                                    value={selectedSeller || ""}
                                    onChange={(e) => setSelectedSeller(e.target.value || null)}
                                >
                                    {sellers.map((seller) => (
                                        <option key={seller} value={seller}>
                                            {seller}
                                        </option>
                                    ))}
                                </select>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Summary Cards */}
            {selectedSeller && (
                <Row className="mb-4">
                    {Object.entries(sellerTotals)
                        .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
                        .map(([year, total]) => (
                            <Col key={year} md={6} lg={3} className="mb-3">
                                <Card className="h-100 border-0 shadow-sm">
                                    <Card.Body>
                                        <div className="text-muted mb-1">
                                            {t("Total Sales")} {year}
                                        </div>
                                        <h3 className="mb-0">{FormatMoney(total, 2)}</h3>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    <Col md={6} lg={3} className="mb-3">
                        <Card className="h-100 border-0 shadow-sm bg-primary text-white">
                            <Card.Body>
                                <div className="mb-1 opacity-75">{t("Grand Total")}</div>
                                <h3 className="mb-0">{FormatMoney(sellerGrandTotal, 2)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Chart */}
            <Row>
                <Col>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <div className="mt-3">{t("Loading sales data")}...</div>
                                </div>
                            ) : error ? (
                                <div className="alert alert-danger" role="alert">
                                    {t("Error")}: {error}
                                </div>
                            ) : !chartData ? (
                                <div className="text-center py-5 text-muted">
                                    {t("No sales data available")}
                                </div>
                            ) : (
                                <div style={{ height: "500px" }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Data Table */}
            {!loading &&
                selectedSeller &&
                chartData &&
                salesData.filter(
                    (d) =>
                        d.vendedorNombre === selectedSeller &&
                        d.month >= 1 &&
                        d.month <= 12 &&
                        d.amount != null
                ).length > 0 && (
                    <Row className="mt-4">
                        <Col>
                            <Card className="border-0 shadow-sm">
                                <Card.Header
                                    className="bg-white d-flex align-items-center justify-content-between"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setShowDetailedData(v => !v)}
                                >
                                    <h5 className="mb-0">{t("Detailed Data")}</h5>
                                    <i className={`fas fa-chevron-${showDetailedData ? "up" : "down"} text-muted`} />
                                </Card.Header>
                                {showDetailedData && (
                                    <Card.Body>
                                        <div className="table-responsive">
                                            <table className="table table-striped table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>{t("Year")}</th>
                                                        <th>{t("Month")}</th>
                                                        <th className="text-end">{t("Amount")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {salesData
                                                        .filter(
                                                            (item) =>
                                                                item.vendedorNombre === selectedSeller &&
                                                                item.year != null &&
                                                                item.month != null &&
                                                                item.month >= 1 &&
                                                                item.month <= 12 &&
                                                                item.amount != null
                                                        )
                                                        .sort((a, b) => {
                                                            if (a.year !== b.year) return b.year - a.year
                                                            return a.month - b.month
                                                        })
                                                        .map((item, index) => (
                                                            <tr key={index}>
                                                                <td>{item.year}</td>
                                                                <td>
                                                                    {new Date(item.year, item.month - 1).toLocaleString(
                                                                        "default",
                                                                        { month: "long" }
                                                                    )}
                                                                </td>
                                                                <td className="text-end">
                                                                    {FormatMoney(item.amount || 0, 2)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Card.Body>
                                )}
                            </Card>
                        </Col>
                    </Row>
                )}

            {/* ── Panel de despacho por vendedor ── */}
            {selectedSeller && (
                <Row className="mt-4">
                    <Col xs={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <h5 className="mb-0">
                                        <i className="fas fa-truck-ramp-box me-2 text-primary" />
                                        {t("Dispatch Activity")} — {selectedSeller}
                                    </h5>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Form.Control
                                            type="date"
                                            size="sm"
                                            value={dispatchStartDate}
                                            onChange={e => setDispatchStartDate(e.target.value)}
                                            style={{ width: 140 }}
                                        />
                                        <span className="text-muted small">—</span>
                                        <Form.Control
                                            type="date"
                                            size="sm"
                                            value={dispatchEndDate}
                                            onChange={e => setDispatchEndDate(e.target.value)}
                                            style={{ width: 140 }}
                                        />
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={() => loadDispatchSummary(selectedSeller, dispatchStartDate, dispatchEndDate)}
                                            disabled={loadingDispatch}
                                        >
                                            {loadingDispatch
                                                ? <span className="spinner-border spinner-border-sm me-1" />
                                                : <i className="fas fa-filter me-1" />}
                                            {t("Apply")}
                                        </button>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loadingDispatch ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" size="sm" />
                                    </div>
                                ) : !dispatchSummary ? (
                                    <p className="text-muted text-center py-3">{t("No dispatch data available")}</p>
                                ) : (
                                    <>
                                        {/* KPIs */}
                                        <Row className="g-3 mb-4">
                                            {[
                                                { label: t("Total Qty Dispatched"), value: dispatchSummary.totalQuantity.toLocaleString(), icon: "fa-boxes-stacked", color: "primary" },
                                                { label: t("Dispatches"), value: dispatchSummary.dispatchCount.toLocaleString(), icon: "fa-truck-ramp-box", color: "success" },
                                                { label: t("Distinct Products"), value: dispatchSummary.distinctProducts.toLocaleString(), icon: "fa-cubes", color: "warning" },
                                                { label: t("Distinct Clients"), value: dispatchSummary.distinctClients.toLocaleString(), icon: "fa-building", color: "info" },
                                            ].map((kpi, i) => (
                                                <Col xs={6} md={3} key={i}>
                                                    <Card className="border-0 bg-light h-100">
                                                        <Card.Body className="d-flex align-items-center gap-2">
                                                            <i className={`fas ${kpi.icon} text-${kpi.color} fa-lg`} />
                                                            <div>
                                                                <div className="text-muted small">{kpi.label}</div>
                                                                <div className="fw-bold">{kpi.value}</div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>

                                        <Row className="g-3">
                                            {/* Categorías */}
                                            <Col md={5}>
                                                <Card className="border-0 bg-light h-100">
                                                    <Card.Header className="bg-transparent border-0 pb-0">
                                                        <span className="fw-semibold small">
                                                            <i className="fas fa-tags me-2 text-success" />
                                                            {t("Top Categories Dispatched")}
                                                        </span>
                                                    </Card.Header>
                                                    <Card.Body className="p-0">
                                                        <Table hover size="sm" className="mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th className="ps-3">{t("Category")}</th>
                                                                    <th className="text-end">{t("Qty")}</th>
                                                                    <th className="text-end pe-3">{t("Dispatches")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {dispatchSummary.byCategory.length === 0 && (
                                                                    <tr><td colSpan={3} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                                                                )}
                                                                {dispatchSummary.byCategory.map((c, i) => (
                                                                    <tr key={i}>
                                                                        <td className="ps-3 small">{c.category}</td>
                                                                        <td className="text-end small fw-semibold">{c.totalQuantity.toLocaleString()}</td>
                                                                        <td className="text-end pe-3"><Badge bg="secondary" className="small">{c.dispatchCount}</Badge></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Card.Body>
                                                </Card>
                                            </Col>

                                            {/* Top productos */}
                                            <Col md={7}>
                                                <Card className="border-0 bg-light h-100">
                                                    <Card.Header className="bg-transparent border-0 pb-0">
                                                        <span className="fw-semibold small">
                                                            <i className="fas fa-boxes me-2 text-warning" />
                                                            {t("Top Products Dispatched")}
                                                        </span>
                                                    </Card.Header>
                                                    <Card.Body className="p-0">
                                                        <Table hover size="sm" className="mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th className="ps-3">{t("Product")}</th>
                                                                    <th>{t("Brand")}</th>
                                                                    <th className="text-end">{t("Qty")}</th>
                                                                    <th className="text-end pe-3">{t("Dispatches")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {dispatchSummary.topProducts.length === 0 && (
                                                                    <tr><td colSpan={4} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                                                                )}
                                                                {dispatchSummary.topProducts.map((p, i) => (
                                                                    <tr key={i}>
                                                                        <td className="ps-3 small">
                                                                            <span className="text-muted me-1">{p.itemCode}</span>
                                                                            {p.description}
                                                                        </td>
                                                                        <td><Badge bg="light" text="dark" className="small">{p.brand || "—"}</Badge></td>
                                                                        <td className="text-end small fw-semibold">{p.totalQuantity.toLocaleString()}</td>
                                                                        <td className="text-end pe-3"><Badge bg="secondary" className="small">{p.dispatchCount}</Badge></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    )
}

export default SalesBySellerPage
